import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import EditCalendarIcon from '@mui/icons-material/EditCalendar';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import ApprovalStatusChip from 'components/chip/ApprovalStatusChip';
import TaskStatusChip from 'components/chip/TaskStatusChip';
import TicketCategoryChip from 'components/chip/TicketCategoryChip';
import Circular from 'components/common/CustomCircular';
import { Condition, Filter } from 'components/condition-list/SearchFilter';
import {
  ConsoleDetailDrawer,
  ConsoleHeader,
  ConsoleSearchFilter,
  useQueryInitialFilters,
} from 'components/console';
import DateTimePicker from 'components/form/DateTimePicker';
import { MasterTypeResult, getMasterType } from 'stores/MasterTypeStoreAdapter';
import {
  ApprovalStatus,
  TaskScheduleInput,
  TaskStatus,
  TicketListResult,
  getTicket,
  listTicket,
  updateTaskSchedule,
} from 'stores/TicketStoreAdapter';
import { TicketTypeSummaryResult, listTicketType } from 'stores/TicketTypeStoreAdapter';
import { SENTINEL_DATE, isSentinelDate } from 'utils/DateTimeFormatter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import TicketDetailView, { useTicketDetail } from './components/TicketDetailView';
import { openTicketInNewTab } from './ticketNavigation';

const LIST_LIMIT = 200;
const DAY_MS = 24 * 60 * 60 * 1000;

/** ガント用に詳細から予定/実績日を取得した行 */
type GanttRow = {
  ticket: TicketListResult;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  actualStart: string | null;
  actualEnd: string | null;
  parentTicketId: string | null;
};

/**
 * ガントチャート（タスク専用・議題10-4）。
 * 予定バー＋実績サブバー、親子まとめ、日付一括編集パネル、9999センチネル対応。
 * CLOSED は表示除外（D5-4）。
 */
const TicketGantt: React.FC = () => {
  const navigate = useNavigate();
  const { users, userSession } = useAuth();
  const drawer = useTicketDetail();

  const [priorities, setPriorities] = useState<MasterTypeResult[]>([]);
  const [types, setTypes] = useState<TicketTypeSummaryResult[]>([]);
  const [parents, setParents] = useState<TicketListResult[]>([]);
  const [rows, setRows] = useState<GanttRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [progress, setProgress] = useState(true);
  const [activeParams, setActiveParams] = useState<Record<string, unknown>>({});
  const [collapsedParents, setCollapsedParents] = useState<Set<string>>(new Set());
  // 日付一括編集モード: ONにするとガントの線領域が DatePicker に切り替わる
  const [bulkEdit, setBulkEdit] = useState(false);
  const [edits, setEdits] = useState<Record<string, TaskScheduleInput>>({});
  const [saving, setSaving] = useState(false);

  // 一覧取得 → 各チケットの詳細を取得して予定/実績日を得る
  const fetchTickets = async (params: Record<string, unknown>) => {
    setProgress(true);
    try {
      const payload: Record<string, unknown> = { ...params, ticketCategory: 'TASK' };
      // IN 検索キーは配列へ正規化
      ['statuses', 'typeIds', 'priorities', 'assigneeUserIds'].forEach((k) => {
        if (payload[k] === undefined || payload[k] === null || payload[k] === '') return;
        if (!Array.isArray(payload[k])) payload[k] = [payload[k]];
      });
      // 親チケットIDで絞り込んだ場合は、その親チケット自身も並列取得してガントに含める
      // （指定した親チケットそのものと子をまとめて表示する）
      const parentId =
        typeof payload.parentTicketId === 'string' ? payload.parentTicketId : undefined;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const [res, parentDetail] = await Promise.all([
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        listTicket(payload as any),
        parentId ? getTicket({ ticketId: parentId }).catch(() => null) : Promise.resolve(null),
      ]);
      // CLOSED を除外
      const visible = res.results.filter((t) => t.status !== 'CLOSED');
      // 各チケットの詳細（予定/実績日）を取得
      const details = await Promise.all(
        visible.map((t) =>
          getTicket({ ticketId: t.ticketId })
            .then((d) => ({
              ticket: t,
              scheduledStart: d.scheduledStartDate,
              scheduledEnd: d.scheduledEndDate,
              actualStart: d.actualStartDate,
              actualEnd: d.actualEndDate,
              parentTicketId: d.parentTicketId,
            }))
            .catch(() => null),
        ),
      );
      const rowsList = details.filter((r): r is GanttRow => r !== null);

      // 親チケット行を先頭に追加（タスク・CLOSED以外・重複排除）
      if (
        parentDetail &&
        parentDetail.ticketCategory === 'TASK' &&
        parentDetail.status !== 'CLOSED' &&
        !rowsList.some((r) => r.ticket.ticketId === parentDetail.ticketId)
      ) {
        rowsList.unshift({
          ticket: {
            ticketId: parentDetail.ticketId,
            managementId: parentDetail.managementId,
            typeId: parentDetail.typeId,
            typeName: parentDetail.typeName,
            ticketCategory: parentDetail.ticketCategory,
            scope: parentDetail.scope,
            keyPrefix: parentDetail.keyPrefix,
            title: parentDetail.title,
            status: parentDetail.status,
            priority: parentDetail.priority,
            assigneeUserId: parentDetail.assigneeUserId,
            assigneeUserName: parentDetail.assigneeUserName,
            approverUserId: parentDetail.approverUserId,
            approverUserName: parentDetail.approverUserName,
            parentTicketId: parentDetail.parentTicketId,
            scheduledStartDate: parentDetail.scheduledStartDate,
            scheduledEndDate: parentDetail.scheduledEndDate,
            actualStartDate: parentDetail.actualStartDate,
            updatedAt: parentDetail.updatedAt,
          },
          scheduledStart: parentDetail.scheduledStartDate,
          scheduledEnd: parentDetail.scheduledEndDate,
          actualStart: parentDetail.actualStartDate,
          actualEnd: parentDetail.actualEndDate,
          parentTicketId: parentDetail.parentTicketId,
        });
      }

      setRows(rowsList);
      setTotalCount(res.totalCount);
    } catch {
      errorDefault('チケットの取得に失敗しました');
    } finally {
      setProgress(false);
    }
  };

  useEffect(() => {
    getMasterType('priority')
      .then(setPriorities)
      .catch(() => setPriorities([]));
    listTicketType({})
      .then(setTypes)
      .catch(() => setTypes([]));
    listTicket({ onlyParent: true })
      .then((res) => setParents(res.results))
      .catch(() => setParents([]));
  }, []);

  const searchConditions: Condition[] = useMemo(
    () => [
      { key: 'keyword', name: 'キーワード', type: 'text' as const, color: '#B0BEC5' },
      {
        key: 'assigneeUserId',
        name: '担当者',
        type: 'user-select-single' as const,
        color: '#F7C8E0',
        users,
      },
      {
        // 同じキーで複数チップ→IN検索（fetchTickets で配列化）
        key: 'typeIds',
        name: '種別',
        type: 'select' as const,
        color: '#B39DDB',
        values: types.map((t) => ({ key: t.typeId, name: t.name })),
      },
      {
        key: 'parentTicketId',
        name: '親チケット',
        type: 'ticket-select-single' as const,
        color: '#A1887F',
        tickets: parents,
      },
      {
        key: 'scope',
        name: 'スコープ',
        type: 'select' as const,
        color: '#80CBC4',
        values: [
          { key: 'GLOBAL', name: 'グローバル' },
          { key: 'PRIVATE', name: 'プライベート' },
        ],
      },
    ],
    [users, types, parents],
  );

  // IN 検索対象キー（同じキーで複数チップ→配列に集約）
  const MULTI_KEYS = ['typeIds', 'statuses', 'priorities', 'assigneeUserIds'];

  const handleSearch = (filters: Filter[]) => {
    const params: Record<string, unknown> = {};
    filters.forEach((f) => {
      if (MULTI_KEYS.includes(f.key)) {
        const arr = (params[f.key] as string[] | undefined) ?? [];
        arr.push(f.value.key);
        params[f.key] = arr;
      } else {
        params[f.key] = f.value.key;
      }
    });
    setActiveParams(params);
    fetchTickets(params);
  };

  // URLクエリ初期条件（ダッシュボード等からの遷移用。self 解決）
  const { queryParams, initialFilters } = useQueryInitialFilters(searchConditions, {
    selfUserId: userSession.userId,
    selfKeys: ['assigneeUserId', 'creatorUserId', 'approverUserId'],
  });

  useEffect(() => {
    setActiveParams(queryParams);
    fetchTickets(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  // 時間軸レンジ:
  // - 最小日 = 開始予定日/開始日 の最小月の1日
  // - 最大日 = 終了予定日/終了日（9999除く）の最大月の末日
  const { axisMin, axisMax } = useMemo(() => {
    const now = new Date();
    const startDates: number[] = [];
    const endDates: number[] = [];
    rows.forEach((r) => {
      if (r.scheduledStart && !isSentinelDate(r.scheduledStart))
        startDates.push(new Date(r.scheduledStart).getTime());
      if (r.actualStart) startDates.push(new Date(r.actualStart).getTime());
      if (r.scheduledEnd && !isSentinelDate(r.scheduledEnd))
        endDates.push(new Date(r.scheduledEnd).getTime());
      if (r.actualEnd) endDates.push(new Date(r.actualEnd).getTime());
    });
    // 何もなければ「今月1日 〜 今月末」を既定に
    const minStart = startDates.length > 0 ? new Date(Math.min(...startDates)) : now;
    const maxEnd = endDates.length > 0 ? new Date(Math.max(...endDates)) : now;
    // 最小月の1日
    const min = new Date(minStart.getFullYear(), minStart.getMonth(), 1).getTime();
    // 最大月の末日（翌月0日 = 当月末）23:59:59
    const max = new Date(maxEnd.getFullYear(), maxEnd.getMonth() + 1, 0, 23, 59, 59).getTime();
    // 最大が最小を下回る異常時は最小月末で丸める
    return { axisMin: min, axisMax: Math.max(max, min + DAY_MS) };
  }, [rows]);

  const span = Math.max(axisMax - axisMin, DAY_MS);
  const pct = (ms: number) => ((ms - axisMin) / span) * 100;

  // 目盛り（1日・15日・月末日）と現在日ラインの位置を算出。
  // ラベルは実際の日付（例: 9/1・9/15・9/30）。翌月の1日が軸内に表示される月は
  // 末日の区切りを出さない（1日と隣接し重複するため）。
  const { ticks, todayPct, todayVisible } = useMemo(() => {
    const result: { pct: number; label: string; strong: boolean }[] = [];
    const start = new Date(axisMin);
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    while (cur.getTime() <= axisMax) {
      const y = cur.getFullYear();
      const m = cur.getMonth();
      const first = new Date(y, m, 1).getTime();
      const mid = new Date(y, m, 15).getTime();
      const lastDate = new Date(y, m + 1, 0);
      const last = lastDate.getTime();
      const nextFirst = new Date(y, m + 1, 1).getTime();
      // 1日（強調）
      if (first >= axisMin && first <= axisMax) {
        result.push({ pct: pct(first), label: `${m + 1}/1`, strong: true });
      }
      // 15日
      if (mid >= axisMin && mid <= axisMax) {
        result.push({ pct: pct(mid), label: `${m + 1}/15`, strong: false });
      }
      // 末日（翌月1日が軸内に出る場合は省略）
      if (last >= axisMin && last <= axisMax && nextFirst > axisMax) {
        result.push({
          pct: pct(last),
          label: `${m + 1}/${lastDate.getDate()}`,
          strong: false,
        });
      }
      cur.setMonth(cur.getMonth() + 1);
    }
    const nowMs = Date.now();
    return {
      ticks: result,
      todayPct: pct(nowMs),
      todayVisible: nowMs >= axisMin && nowMs <= axisMax,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axisMin, axisMax]);

  // 親子まとめ: 親→子の順に並べる。単独は1行。
  const orderedRows = useMemo(() => {
    const byParent = new Map<string, GanttRow[]>();
    const parents: GanttRow[] = [];
    const standalone: GanttRow[] = [];
    const ids = new Set(rows.map((r) => r.ticket.ticketId));
    rows.forEach((r) => {
      if (r.parentTicketId && ids.has(r.parentTicketId)) {
        const arr = byParent.get(r.parentTicketId) ?? [];
        arr.push(r);
        byParent.set(r.parentTicketId, arr);
      } else if (rows.some((c) => c.parentTicketId === r.ticket.ticketId)) {
        parents.push(r);
      } else {
        standalone.push(r);
      }
    });
    const result: { row: GanttRow; depth: number; isParent: boolean }[] = [];
    parents.forEach((p) => {
      result.push({ row: p, depth: 0, isParent: true });
      if (!collapsedParents.has(p.ticket.ticketId)) {
        (byParent.get(p.ticket.ticketId) ?? []).forEach((c) =>
          result.push({ row: c, depth: 1, isParent: false }),
        );
      }
    });
    standalone.forEach((s) => result.push({ row: s, depth: 0, isParent: false }));
    return result;
  }, [rows, collapsedParents]);

  const toggleParent = (id: string) =>
    setCollapsedParents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // バー描画（予定 + 実績）
  const renderBars = (r: GanttRow) => {
    const bars: React.ReactNode[] = [];
    const sStart =
      r.scheduledStart && !isSentinelDate(r.scheduledStart)
        ? new Date(r.scheduledStart).getTime()
        : null;
    const sEndSentinel = isSentinelDate(r.scheduledEnd);
    const sEnd = r.scheduledEnd && !sEndSentinel ? new Date(r.scheduledEnd).getTime() : null;

    // 予定バー（開始予定日〜終了予定日）。
    // 終了予定なし(9999) は終端未定として、軸右端へフェードする特殊バーで描く（破線は付けない）。
    if (sStart !== null) {
      const end = sEnd !== null ? sEnd : axisMax;
      const left = pct(sStart);
      const width = Math.max(pct(end) - left, 0.5);
      bars.push(
        <Box
          key="sched"
          sx={{
            position: 'absolute',
            top: 6,
            left: `${left}%`,
            width: `${width}%`,
            height: 10,
            borderRadius: 1,
            bgcolor: 'primary.main',
            ...(sEndSentinel && {
              bgcolor: 'transparent',
              background: (t) =>
                `linear-gradient(90deg, ${t.palette.primary.main} 0%, ${t.palette.primary.main} 55%, transparent 100%)`,
            }),
          }}
        />,
      );
    }
    // 実績サブバー
    if (r.actualStart) {
      const aStart = new Date(r.actualStart).getTime();
      const aEnd = r.actualEnd ? new Date(r.actualEnd).getTime() : Date.now();
      const left = pct(aStart);
      const width = Math.max(pct(aEnd) - left, 1);
      bars.push(
        <Box
          key="actual"
          sx={{
            position: 'absolute',
            top: 18,
            left: `${left}%`,
            width: `${width}%`,
            height: 6,
            borderRadius: 1,
            bgcolor: 'success.main',
          }}
        />,
      );
    }
    return bars;
  };

  // 日付一括編集
  const setEdit = (ticketId: string, patch: Partial<TaskScheduleInput>) =>
    setEdits((prev) => ({ ...prev, [ticketId]: { ...prev[ticketId], ...patch, ticketId } }));

  const handleSaveSchedules = () => {
    const editedIds = Object.keys(edits);
    if (editedIds.length === 0 || saving) return;
    // API7113 は指定された4項目をそのまま UPDATE する（未指定はクリアされる）。
    // そのため未編集の項目は現在値を明示的に載せて全項目を送る。
    // 終了予定日が未設定の場合はセンチネル(9999-12-31)を送って「未設定」を維持する。
    const schedules: TaskScheduleInput[] = editedIds.map((ticketId) => {
      const row = rows.find((r) => r.ticket.ticketId === ticketId);
      const e = edits[ticketId] ?? { ticketId };
      const pick = (
        key: 'scheduledStartDate' | 'scheduledEndDate' | 'actualStartDate' | 'actualEndDate',
        current: string | null | undefined,
      ) => (e[key] !== undefined ? e[key] : (current ?? undefined));
      const schedEnd = pick('scheduledEndDate', row?.scheduledEnd);
      return {
        ticketId,
        scheduledStartDate: pick('scheduledStartDate', row?.scheduledStart) || undefined,
        // 空 → 未設定センチネルで維持
        scheduledEndDate: schedEnd || `${SENTINEL_DATE}T23:59:59`,
        actualStartDate: pick('actualStartDate', row?.actualStart) || undefined,
        actualEndDate: pick('actualEndDate', row?.actualEnd) || undefined,
      };
    });
    setSaving(true);
    updateTaskSchedule({ schedules })
      .then(() => {
        successDefault('日付を保存しました');
        setEdits({});
        setBulkEdit(false);
        fetchTickets(activeParams);
      })
      .catch((e) => errorDefault('日付の保存に失敗しました', toErrorDetail(e)))
      .finally(() => setSaving(false));
  };

  const cancelBulkEdit = () => {
    setEdits({});
    setBulkEdit(false);
  };

  // 行の編集値（未編集なら現在値・センチネルは空）を返す
  const editValue = (r: GanttRow, key: keyof TaskScheduleInput): string => {
    const e = edits[r.ticket.ticketId];
    if (e && e[key] !== undefined) return (e[key] as string) ?? '';
    switch (key) {
      case 'scheduledStartDate':
        return r.scheduledStart && !isSentinelDate(r.scheduledStart) ? r.scheduledStart : '';
      case 'scheduledEndDate':
        return r.scheduledEnd && !isSentinelDate(r.scheduledEnd) ? r.scheduledEnd : '';
      case 'actualStartDate':
        return r.actualStart ?? '';
      case 'actualEndDate':
        return r.actualEnd ?? '';
      default:
        return '';
    }
  };

  const overLimit = totalCount > LIST_LIMIT;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <ConsoleHeader
        title="ガントチャート"
        count={totalCount}
        description="タスクの予定/実績を時間軸で表示します（CLOSEDは除外）"
        backLabel="チケット管理"
        onBack={() => navigate('/ticket')}
        onRefresh={() => fetchTickets(activeParams)}
        actions={
          bulkEdit ? (
            <Stack direction="row" spacing={1} alignItems="center">
              {Object.keys(edits).length > 0 && (
                <Typography variant="caption" color="primary.main">
                  {Object.keys(edits).length}件 変更中
                </Typography>
              )}
              <Button
                size="small"
                onClick={cancelBulkEdit}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<SaveIcon />}
                disabled={Object.keys(edits).length === 0 || saving}
                onClick={handleSaveSchedules}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                まとめて保存
              </Button>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditCalendarIcon />}
              onClick={() => setBulkEdit(true)}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              スケジュールを一括編集
            </Button>
          )
        }
      />

      <Box sx={{ mb: 1 }}>
        <ConsoleSearchFilter
          conditions={searchConditions}
          initialFilters={initialFilters}
          onFiltersChange={handleSearch}
          conditionKey="ticket-gantt"
          userProfileKey="ticket-gantt-search"
        />
      </Box>

      {overLimit && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mb: 1 }}>
          他 {totalCount - rows.length} 件は表示されていません。検索条件で絞り込んでください。
        </Typography>
      )}

      {/* ガント本体 */}
      <Box sx={{ flex: 1, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
        {orderedRows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              表示するタスクがありません
            </Typography>
          </Box>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {/* 日付目盛りヘッダー（起点表示） */}
            {!bulkEdit && (
              <Stack
                direction="row"
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 3,
                  bgcolor: 'background.paper',
                  borderBottom: 1,
                  borderColor: 'divider',
                  minHeight: 28,
                }}
              >
                <Box
                  sx={{ width: 260, flexShrink: 0, px: 1, display: 'flex', alignItems: 'center' }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    タスク
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, position: 'relative', minWidth: 400 }}>
                  {ticks.map((t, i) => (
                    <Typography
                      key={i}
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        left: `${t.pct}%`,
                        top: 4,
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                        fontSize: 10,
                        fontWeight: t.strong ? 700 : 400,
                        color: t.strong ? 'text.primary' : 'text.secondary',
                      }}
                    >
                      {t.label}
                    </Typography>
                  ))}
                </Box>
              </Stack>
            )}

            {/* 目盛り縦線（タイムライン領域の背面にオーバーレイ） */}
            {!bulkEdit && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 260,
                  right: 0,
                  top: 28,
                  bottom: 0,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              >
                {ticks.map((t, i) => (
                  <Box
                    key={i}
                    sx={{
                      position: 'absolute',
                      left: `${t.pct}%`,
                      top: 0,
                      bottom: 0,
                      // 1日は実線でしっかり、15日/末日は破線で。従来より濃く目立たせる
                      borderLeft: t.strong ? 2 : 1,
                      borderLeftStyle: t.strong ? 'solid' : 'dashed',
                      borderColor: t.strong ? 'text.disabled' : 'divider',
                      opacity: t.strong ? 1 : 0.8,
                    }}
                  />
                ))}
              </Box>
            )}

            {/* 現在日ライン（バーより前面に表示） */}
            {!bulkEdit && todayVisible && (
              <Box
                sx={{
                  position: 'absolute',
                  left: 260,
                  right: 0,
                  top: 28,
                  bottom: 0,
                  pointerEvents: 'none',
                  zIndex: 4,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${todayPct}%`,
                    top: 0,
                    bottom: 0,
                    borderLeft: 2,
                    borderColor: 'error.main',
                  }}
                />
              </Box>
            )}

            {orderedRows.map(({ row, depth, isParent }) => (
              <Stack
                key={row.ticket.ticketId}
                direction="row"
                alignItems="center"
                sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
              >
                {/* 左: タスク名 */}
                <Box
                  sx={{
                    width: 260,
                    flexShrink: 0,
                    px: 1,
                    pl: depth * 2 + 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  {isParent && (
                    <IconButton
                      size="small"
                      onClick={() => toggleParent(row.ticket.ticketId)}
                      sx={{ p: 0.25 }}
                    >
                      {collapsedParents.has(row.ticket.ticketId) ? (
                        <ExpandMoreIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <ExpandLessIcon sx={{ fontSize: 18 }} />
                      )}
                    </IconButton>
                  )}
                  <Tooltip
                    title={`${row.ticket.title}（${row.ticket.managementId}）`}
                    placement="top-start"
                  >
                    <Stack
                      direction="row"
                      alignItems="baseline"
                      spacing={0.75}
                      onClick={() => drawer.load(row.ticket.ticketId)}
                      sx={{
                        minWidth: 0,
                        cursor: 'pointer',
                        '&:hover .gantt-title': { color: 'primary.main' },
                      }}
                    >
                      <Typography
                        className="gantt-title"
                        variant="body2"
                        noWrap
                        sx={{ fontWeight: isParent ? 700 : 500, minWidth: 0 }}
                      >
                        {row.ticket.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{ flexShrink: 0 }}
                      >
                        {row.ticket.managementId}
                      </Typography>
                    </Stack>
                  </Tooltip>
                </Box>
                {/* 右: タイムライン（一括編集モードでは DatePicker 群に切替） */}
                {bulkEdit ? (
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 400,
                      px: 1.25,
                      py: 1,
                      // 変更のある行はアクセントの左線＋淡い背景で「編集済み」を明示
                      borderLeft: 3,
                      borderColor: edits[row.ticket.ticketId] ? 'primary.main' : 'transparent',
                      borderRadius: 1,
                      bgcolor: edits[row.ticket.ticketId] ? 'action.hover' : 'transparent',
                      transition: 'background-color 0.15s, border-color 0.15s',
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      flexWrap="wrap"
                      useFlexGap
                      alignItems="flex-start"
                    >
                      <DateRangeGroup
                        label="予定"
                        color="primary.main"
                        startValue={editValue(row, 'scheduledStartDate')}
                        endValue={editValue(row, 'scheduledEndDate')}
                        onStartChange={(v) =>
                          setEdit(row.ticket.ticketId, { scheduledStartDate: v })
                        }
                        onEndChange={(v) =>
                          setEdit(row.ticket.ticketId, {
                            scheduledEndDate: v || `${SENTINEL_DATE}T23:59:59`,
                          })
                        }
                      />
                      <DateRangeGroup
                        label="実績"
                        color="success.main"
                        startValue={editValue(row, 'actualStartDate')}
                        endValue={editValue(row, 'actualEndDate')}
                        onStartChange={(v) => setEdit(row.ticket.ticketId, { actualStartDate: v })}
                        onEndChange={(v) => setEdit(row.ticket.ticketId, { actualEndDate: v })}
                      />
                    </Stack>
                  </Box>
                ) : (
                  <Box sx={{ flex: 1, position: 'relative', height: 34, minWidth: 400 }}>
                    {renderBars(row)}
                  </Box>
                )}
              </Stack>
            ))}
          </Box>
        )}
      </Box>

      {bulkEdit && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          各行の日付を編集し「まとめて保存」で一括更新します。
        </Typography>
      )}

      {/* 凡例 */}
      <Stack direction="row" spacing={2} sx={{ mt: 0.5, px: 0.5 }}>
        <LegendItem color="primary.main" label="予定" />
        <LegendItem color="success.main" label="実績" />
        <Typography variant="caption" color="text.secondary">
          フェード = 終端未定（終了予定 未設定）
        </Typography>
      </Stack>

      <ConsoleDetailDrawer
        open={drawer.detail !== null}
        onClose={drawer.clear}
        title={drawer.detail?.managementId ?? ''}
        badges={
          drawer.detail && (
            <>
              <TicketCategoryChip ticketCategory={drawer.detail.ticketCategory} />
              {drawer.detail.ticketCategory === 'TASK' ? (
                <TaskStatusChip status={drawer.detail.status as TaskStatus} />
              ) : (
                <ApprovalStatusChip status={drawer.detail.status as ApprovalStatus} />
              )}
            </>
          )
        }
        width="min(80vw, 1100px)"
      >
        {drawer.detail && (
          <Box sx={{ height: '100%', mx: -2.5, mb: -2.5 }}>
            <TicketDetailView
              detail={drawer.detail}
              priorities={priorities}
              onReload={() => {
                if (drawer.detail) drawer.load(drawer.detail.ticketId);
                fetchTickets(activeParams);
              }}
              onOpenTicket={openTicketInNewTab}
            />
          </Box>
        )}
      </ConsoleDetailDrawer>

      <Circular open={progress} />
    </Box>
  );
};

/**
 * 日付一括編集の「予定 / 実績」ペア入力。
 * 小さな色ラベル＋ドットで区分を示し、開始 〜 終了 を1組で並べて範囲であることを直感的にする。
 */
const DateRangeGroup: React.FC<{
  label: string;
  color: string;
  startValue: string;
  endValue: string;
  onStartChange: (v: string) => void;
  onEndChange: (v: string) => void;
}> = ({ label, color, startValue, endValue, onStartChange, onEndChange }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        {label}
      </Typography>
    </Stack>
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <DateTimePicker
        mode="start"
        label="開始"
        value={startValue}
        onChange={onStartChange}
        size="small"
      />
      <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
        〜
      </Typography>
      <DateTimePicker
        mode="end"
        label="終了"
        value={endValue}
        onChange={onEndChange}
        size="small"
      />
    </Stack>
  </Box>
);

const LegendItem: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <Stack direction="row" spacing={0.5} alignItems="center">
    <Box sx={{ width: 14, height: 8, borderRadius: 0.5, bgcolor: color }} />
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Stack>
);

export default TicketGantt;
